import { Body, Box, Heading } from '@hover/blueprint';
import React, { useState } from 'react';

import type { ImageLike } from 'src/modules/renderings/imageLike';
import type { Rendering, RenderingVersion } from 'src/modules/renderings/types';
import { HiDefRenderingVersionState } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { AccordionItem } from './AccordionItem';
import { AccordionItemContent } from './AccordionItemContent';

interface RenderingsAccordionProps {
  children: React.ReactNode;
  testId?: string;
}

const getNumberWithSuffix = (number: number): string => {
  const suffixArray = ['th', 'st', 'nd', 'rd'];
  const index =
    (number % 100) - (number % 10) - 10 && number % 10 < 4 ? number % 10 : 0;

  return number.toString() + suffixArray[index];
};

const RevisionOrdered = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  return (
    <Box
      alignItems="center"
      background="primary.100"
      flexDir="column"
      marginTop="400"
      padding="500"
      testId="revision-pending-message"
    >
      <Heading size={200}>{t('renderings.revisionOrdered')}</Heading>
      <Body margin={0} size={400} textAlign="center">
        {t('renderings.revisionBlurb')}
      </Body>
    </Box>
  );
};

const calcDefaultExpandedIndex = (
  versions: RenderingVersion[],
  selectedImageId?: string,
) => {
  if (selectedImageId) {
    for (let i = 0; i < versions.length; i += 1) {
      const version = versions[i];
      if (
        version.hiDefRenderingFiles.some(
          file => file.image.attachmentId === selectedImageId,
        )
      ) {
        return i;
      }
    }
  }

  return 0;
};

const InternalRenderingVersions = ({
  heroImageURL,
  rendering,
  handleImageSelect,
  name,
  selectedImageId,
}: {
  heroImageURL?: string;
  rendering: Rendering;
  handleImageSelect: Function;
  name?: string;
  selectedImageId?: string;
}) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const versions = [...rendering.versions].sort((a, b) => b.id - a.id);
  const numberOfSets = versions.length;
  const [expandedIndex, setExpandedIndex] = useState(() =>
    calcDefaultExpandedIndex(versions, selectedImageId),
  );

  const handleToggleExpanded = (idx: number) => {
    if (expandedIndex === idx) {
      setExpandedIndex(-1);
    } else {
      setExpandedIndex(idx);
    }
  };

  return (
    <>
      {versions.map((version, idx) => {
        const collectionNumber = numberOfSets - idx;
        const title = t('renderings.designSet', {
          number: collectionNumber,
        });
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const downloadFileName = name ? `${name} - ${title}` : title;
        const versionImages = version.hiDefRenderingFiles.map(
          file => file.image,
        );

        if (!version.submissionUrl && !version.hiDefRenderingFiles.length) {
          return null;
        }

        let content = <RevisionOrdered />;
        if (version.state === HiDefRenderingVersionState.Complete) {
          content = (
            <AccordionItemContent
              downloadFileName={downloadFileName}
              handleImageSelect={handleImageSelect}
              heroImageURL={heroImageURL}
              images={versionImages}
              initialItemMax={9}
              title={title}
              versionId={version.id?.toString()}
            />
          );
        }
        return (
          <AccordionItem
            isActive={expandedIndex === idx}
            key={title}
            lastItem={idx === versions.length - 1}
            onToggleExpanded={() => handleToggleExpanded(idx)}
            title={title}
            versionId={version.id?.toString()}
          >
            {content}
          </AccordionItem>
        );
      })}
    </>
  );
};

const RenderingVersions = ({
  renderings,
  ...props
}: {
  heroImageURL?: string;
  renderings: Rendering[];
  handleImageSelect: Function;
  name?: string;
  selectedImageId?: string;
}) => {
  const rendering = renderings[0];
  if (!rendering) {
    return null;
  }

  if (rendering.versions.length === 0) {
    return null;
  }

  return <InternalRenderingVersions rendering={rendering} {...props} />;
};

const JobImageItems = ({
  heroImageURL,
  images,
  handleImageSelect,
  name,
}: {
  heroImageURL: string;
  images: ImageLike[];
  handleImageSelect: Function;
  name?: string;
}) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const title = t('renderings.originalPhotos');
  const downloadFileName = name ? `${name} - ${title}` : title;
  const [isActive, setIsActive] = useState(true);

  return (
    <AccordionItem
      isActive={isActive}
      key={title}
      lastItem
      onToggleExpanded={() => setIsActive(!isActive)}
      title={title}
    >
      <AccordionItemContent
        downloadFileName={downloadFileName}
        handleImageSelect={handleImageSelect}
        heroImageURL={heroImageURL}
        hideDownloadButton
        images={images}
        initialItemMax={9}
        title={title}
      />
    </AccordionItem>
  );
};

const RenderingsAccordion = ({
  children,
  testId,
}: RenderingsAccordionProps) => (
  <Box
    backgroundColor="neutral.100"
    borderRadius="5px"
    display="flex"
    flexDirection="column"
    marginBottom={400}
    testId={testId}
  >
    {children}
  </Box>
);

export {
  JobImageItems,
  RenderingVersions,
  RenderingsAccordion,
  getNumberWithSuffix,
};
